import { useEffect, useState } from "react";
import { listTenants, patchTenant, tierNow, triggerScan, archiveRun, enrollBackup, type TenantRow } from "../api";

export function Tenants() {
  const [tenants, setTenants] = useState<TenantRow[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { void reload(); }, []);
  async function reload() {
    try {
      const r = await listTenants();
      setTenants(r.tenants);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "load failed");
    }
  }

  async function runOp(label: string, op: () => Promise<unknown>) {
    setBusy(label); setMsg(null); setErr(null);
    try {
      await op();
      setMsg(`${label}: ok`);
      await reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : `${label} failed`);
    } finally { setBusy(null); }
  }

  if (!tenants) return <div>Loading tenants…</div>;

  return (
    <>
      <div className="row spaced">
        <h2>Tenants</h2>
        <button className="btn ghost" onClick={() => reload()}>Refresh</button>
      </div>
      {msg && <div className="ok">{msg}</div>}
      {err && <div className="err">{err}</div>}
      <table>
        <thead>
          <tr>
            <th>Tenant</th>
            <th>Backup</th>
            <th>Archive</th>
            <th>Glacier</th>
            <th>Archive policy</th>
            <th>Files</th>
            <th>Savings (AUD/mo)</th>
            <th>Mode</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {tenants.map(t => (
            <tr key={t.tenant_id}>
              <td>
                <strong>{t.display_name}</strong>
                <div className="mono muted">{t.tenant_id}</div>
              </td>
              <td>
                <Toggle
                  on={t.backup_enabled === 1}
                  busy={busy === `backup:${t.tenant_id}`}
                  onChange={v => runOp(`backup:${t.tenant_id}`, () => patchTenant(t.tenant_id, { backup_enabled: v ? 1 : 0 }))}
                />
              </td>
              <td>
                <Toggle
                  on={t.archive_enabled === 1}
                  busy={busy === `archive:${t.tenant_id}`}
                  onChange={v => runOp(`archive:${t.tenant_id}`, () => patchTenant(t.tenant_id, { archive_enabled: v ? 1 : 0 }))}
                />
              </td>
              <td>
                <Toggle
                  on={t.glacier_enabled === 1}
                  busy={busy === `glacier:${t.tenant_id}`}
                  onChange={v => runOp(`glacier:${t.tenant_id}`, () => patchTenant(t.tenant_id, { glacier_enabled: v ? 1 : 0 }))}
                />
              </td>
              <td>
                <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <NumberField label="trigger" suffix="%" value={t.archive_trigger_pct ?? 85} busy={busy?.startsWith(`pol:${t.tenant_id}`) ?? false}
                    onCommit={v => runOp(`pol:${t.tenant_id}:trigger`, () => patchTenant(t.tenant_id, { archive_trigger_pct: v }))} />
                  <NumberField label="target" suffix="%" value={t.archive_target_pct ?? 70} busy={busy?.startsWith(`pol:${t.tenant_id}`) ?? false}
                    onCommit={v => runOp(`pol:${t.tenant_id}:target`, () => patchTenant(t.tenant_id, { archive_target_pct: v }))} />
                  <NumberField label="idle" suffix="d" value={t.archive_min_idle_days ?? 180} busy={busy?.startsWith(`pol:${t.tenant_id}`) ?? false}
                    onCommit={v => runOp(`pol:${t.tenant_id}:idle`, () => patchTenant(t.tenant_id, { archive_min_idle_days: v }))} />
                  <label className="muted" style={{ fontSize: 12 }}>
                    <input type="checkbox" checked={(t.backup_capture_native_versions ?? 1) === 1}
                      onChange={e => runOp(`nv:${t.tenant_id}`, () => patchTenant(t.tenant_id, { backup_capture_native_versions: e.target.checked ? 1 : 0 }))} />
                    native vers
                  </label>
                </div>
              </td>
              <td>{t.total_files_archived.toLocaleString()}</td>
              <td>${t.estimated_monthly_saving_aud.toFixed(2)}</td>
              <td>
                <span className={"badge " + (t.archive_mode === "delete" ? "red" : "amber")}>
                  {t.archive_mode}
                </span>
              </td>
              <td>
                <div className="row" style={{ flexWrap: "wrap", gap: 6 }}>
                  <button className="btn ghost" disabled={busy === `scan:${t.tenant_id}`}
                    onClick={() => runOp(`scan:${t.tenant_id}`, () => triggerScan(t.tenant_id))}>
                    Scan
                  </button>
                  <button className="btn ghost" disabled={busy === `arch:${t.tenant_id}` || t.archive_enabled !== 1}
                    onClick={() => runOp(`arch:${t.tenant_id}`, () => archiveRun(t.tenant_id))}>
                    Archive now
                  </button>
                  <button className="btn ghost" disabled={busy === `tier:${t.tenant_id}` || t.glacier_enabled !== 1}
                    onClick={() => runOp(`tier:${t.tenant_id}`, () => tierNow(t.tenant_id))}>
                    Tier
                  </button>
                  <EnrolForm
                    disabled={t.backup_enabled !== 1}
                    busy={busy === `enrol:${t.tenant_id}`}
                    onEnrol={(site, drive) => runOp(`enrol:${t.tenant_id}`, () => enrollBackup(t.tenant_id, site, drive))}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function Toggle({ on, busy, onChange }: { on: boolean; busy: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      className={"badge " + (on ? "green" : "grey")}
      style={{ border: "none", cursor: busy ? "wait" : "pointer" }}
      disabled={busy}
      onClick={() => onChange(!on)}
    >
      {busy ? "…" : (on ? "On" : "Off")}
    </button>
  );
}

function NumberField(
  { label, suffix, value, busy, onCommit }:
  { label: string; suffix?: string; value: number; busy: boolean; onCommit: (v: number) => void }
) {
  const [v, setV] = useState(String(value));
  useEffect(() => { setV(String(value)); }, [value]);
  function commit() {
    const n = parseInt(v, 10);
    if (Number.isFinite(n) && n !== value) onCommit(n);
  }
  return (
    <span className="row" style={{ gap: 2, alignItems: "center" }} title={label}>
      <span className="muted" style={{ fontSize: 11 }}>{label}</span>
      <input
        className="input"
        style={{ width: 48, padding: "2px 4px" }}
        value={v}
        disabled={busy}
        onChange={e => setV(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") commit(); }}
        onBlur={commit}
      />
      {suffix}
    </span>
  );
}

function EnrolForm({ disabled, busy, onEnrol }: { disabled: boolean; busy: boolean; onEnrol: (site: string, drive: string) => void }) {
  const [site, setSite] = useState("");
  const [drive, setDrive] = useState("");
  return (
    <span className="row" style={{ gap: 2, alignItems: "center" }}>
      <input className="input" style={{ width: 90, padding: "2px 4px" }} placeholder="site id" value={site} disabled={disabled || busy} onChange={e => setSite(e.target.value)} />
      <input className="input" style={{ width: 90, padding: "2px 4px" }} placeholder="drive id" value={drive} disabled={disabled || busy} onChange={e => setDrive(e.target.value)} />
      <button className="btn ghost" disabled={disabled || busy || !site.trim() || !drive.trim()} onClick={() => onEnrol(site.trim(), drive.trim())}>
        {busy ? "…" : "Enrol"}
      </button>
    </span>
  );
}
